// Fyrefly permission hooks — org-scoped RBAC
// Wildcard semantics: '*' grants everything; 'domain.*' grants domain-scoped perms.
// hasPermission() must match the SQL get_my_permissions() wildcard logic.

import { createContext, useContext } from 'react';

export type PermissionContextType = {
  permissions: Set<string>;
  loading: boolean;
};

export const PermissionContext = createContext<PermissionContextType>({
  permissions: new Set(),
  loading: true,
});

/** True if the user has the required permission (with wildcard expansion). */
export function usePermission(required: string): boolean {
  const { permissions, loading } = useContext(PermissionContext);
  if (loading) return false;
  return hasPermission(permissions, required);
}

/** True if the user has ALL of the required permissions. */
export function usePermissions(...required: string[]): boolean {
  const { permissions, loading } = useContext(PermissionContext);
  if (loading) return false;
  return required.every((p) => hasPermission(permissions, p));
}

/** True if the user has ANY of the required permissions. */
export function useAnyPermission(...required: string[]): boolean {
  const { permissions, loading } = useContext(PermissionContext);
  if (loading) return false;
  return required.some((p) => hasPermission(permissions, p));
}

export function usePermissionLoading(): boolean {
  return useContext(PermissionContext).loading;
}

/** Check a single permission with wildcard expansion.
 *  '*' matches everything; 'domain.*' matches 'domain.anything'. */
export function hasPermission(granted: Set<string>, required: string): boolean {
  if (granted.has('*')) return true;
  if (granted.has(required)) return true;
  const domain = required.split('.')[0];
  if (granted.has(domain + '.*')) return true;
  return false;
}
